import { Injectable, NgZone, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, map } from 'rxjs';

import { BaseService } from 'src/app/@core/services/base.service';
import { STATUS_TYPE } from 'src/app/@core/models/model';
import { StatusService } from 'src/app/contract-management/services/status.service';
import { environment } from 'src/environments/environment.dev';

import {
    AskBobStandaloneData,
    BobAssistedHoursData,
    BobSurfaceUsage,
    DateRange,
    DocumentCountsData,
    DocumentsEnhancedData,
    FeedbackQualityData,
    PeriodFilter,
    ProjectsData,
    TimeReclaimedBreakdown,
    TimeReclaimedData,
    UntappedPotentialData,
    WorkspaceViewMode,
} from '../models/workspace-overview.model';


interface TimeReclaimedApiResponse {
    breakdown: TimeReclaimedBreakdown[];
    totalMinutesReclaimed: number;
    totalHoursReclaimed: number;
    totalCostSaved: number;
    percentageChangeFromLastMonth: number | null;
}

interface BobAssistedHoursApiResponse {
    totalHours: number;
    breakdown: Array<{
        surface: string;
        threadType: string;
        hours: number;
        minutes: number;
    }>;
}

interface AskBobStandaloneApiResponse {
    conversations: number;
    documentsAnalyzed: number;
    outputs: { total: number; pdf: number; docx: number; xlsx: number, others: number };
    modeSplit: { ask: number; agentic: number };
}



type OverviewRequestBody = {
    scope: WorkspaceViewMode;
    workspaceId?: string;
    startDate: string;
    endDate: string;
};

const buildRequestBody = (params: WidgetQueryParams): OverviewRequestBody => {
    const base = { scope: params.mode, workspaceId: params.workspaceId };
    const now = new Date();
    const endOfDay = new Date(now);
    endOfDay.setHours(23, 59, 59, 999);

    if (params.period === PeriodFilter.Custom && params.range) {
        return {
            ...base,
            startDate: params.range.from.toISOString(),
            endDate: params.range.to.toISOString(),
        };
    }

    if (params.period === PeriodFilter.Week) {
        const start = new Date(now);
        start.setDate(start.getDate() - 6);
        return { ...base, startDate: start.toISOString(), endDate: endOfDay.toISOString() };
    }

    if (params.period === PeriodFilter.Month) {
        const start = new Date(now);
        start.setDate(start.getDate() - 29);
        return { ...base, startDate: start.toISOString(), endDate: endOfDay.toISOString() };
    }

    // Quarter
    const start = new Date(now);
    start.setMonth(start.getMonth() - 3);
    return { ...base, startDate: start.toISOString(), endDate: endOfDay.toISOString() };
};


export interface WidgetQueryParams {
    mode: WorkspaceViewMode;
    period: PeriodFilter;
    range?: DateRange | null;
    workspaceId?: string;
}

@Injectable({ providedIn: 'root' })
export class WorkspaceOverviewService extends BaseService {

    private apiUrl = environment.apiUrl + '/workspace-overview';

    private readonly statusService = inject(StatusService);

    constructor() {
        super(inject(HttpClient), inject(NgZone));
    }

    getTimeReclaimed(params: WidgetQueryParams): Observable<TimeReclaimedData> {
        return this.post<TimeReclaimedApiResponse>(`${this.apiUrl}/time-reclaimed`, buildRequestBody(params)).pipe(
            map(res => ({
                reclaimedHours: res.data.totalHoursReclaimed,
                manualHours: res.data.totalHoursReclaimed,
                costSaved: res.data.totalCostSaved,
                breakdown: res.data.breakdown,
                percentageChangeFromLastMonth: res.data.percentageChangeFromLastMonth,
            } satisfies TimeReclaimedData)),
        );
    }

    getBobAssistedHours(params: WidgetQueryParams): Observable<BobAssistedHoursData> {
        return this.post<BobAssistedHoursApiResponse>(`${this.apiUrl}/bob-assisted-hours`, buildRequestBody(params)).pipe(
            map(res => {
                const bob = res.data;
                const total = bob.totalHours || 1;
                const surfaces: BobSurfaceUsage[] = bob.breakdown.map(s => ({
                    name: s.surface,
                    hours: s.hours,
                    percent: Math.round((s.hours / total) * 100),
                }));
                return { totalHours: bob.totalHours, surfaces };
            }),
        );
    }

    getDocumentsEnhanced(params: WidgetQueryParams): Observable<DocumentsEnhancedData> {
        return this.post<DocumentsEnhancedData>(`${this.apiUrl}/documents-enhanced`, buildRequestBody(params)).pipe(
            map(res => res.data),
        );
    }

    getAskBobStandalone(params: WidgetQueryParams): Observable<AskBobStandaloneData> {
        return this.post<AskBobStandaloneApiResponse>(`${this.apiUrl}/bob-standalone`, buildRequestBody(params)).pipe(
            map(res => res.data satisfies AskBobStandaloneData),
        );
    }

    getUntappedPotential(params: WidgetQueryParams): Observable<UntappedPotentialData> {
        return this.post<UntappedPotentialData>(`${this.apiUrl}/untapped-potential`, buildRequestBody(params)).pipe(
            map(res => res.data),
        );
    }

    getFeedbackQuality(params: WidgetQueryParams): Observable<FeedbackQualityData> {
        return this.post<FeedbackQualityData>(`${this.apiUrl}/feedback-quality`, buildRequestBody(params)).pipe(
            map(res => res.data),
        );
    }

    getProjects(params: { mode: WorkspaceViewMode; workspaceId?: string }): Observable<ProjectsData> {
        return forkJoin([
            this.post<ProjectsData>(`${this.apiUrl}/projects`, {
                scope: params.mode,
                workspaceId: params.workspaceId,
            }),
            this.statusService.getStatuses(STATUS_TYPE.PROJECT),
        ]).pipe(
            map(([projectsRes, statusesRes]) => {
                const countsMap = new Map(
                    (projectsRes.data.statuses ?? []).map(s => [s.name, s.count])
                );
                const statuses = (statusesRes.data ?? [])
                    .filter(s => s.active)
                    .map(s => ({ name: s.name, count: countsMap.get(s.name) ?? 0 }));
                return { statuses };
            }),
        );
    }

    getDocumentCounts(params: { mode: WorkspaceViewMode }): Observable<DocumentCountsData> {
        return this.post<DocumentCountsData>(`${this.apiUrl}/document-counts`, { scope: params.mode }).pipe(
            map(res => res.data),
        );
    }
}
